/**
 * AccountsGSTPage - GST & India Taxes Module
 * Comprehensive GST management with AI reports and automation
 */
import { useState } from 'react';
import { gstAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  FileText, Calculator, TrendingUp,
  RefreshCw, Upload, Settings, PieChart, Lightbulb,
  IndianRupee, FileSpreadsheet, Truck
} from 'lucide-react';
import { PageSkeleton } from '../components/shared/LoadingSkeleton';
import { DataTable } from '../components/shared/DataTable';

const GST_RATES = [0, 5, 12, 18, 28];
const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

export default function AccountsGSTPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const canManage = isAdmin || isSuperAdmin;
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  // configDialogOpen available for future settings dialog
  const [_configDialogOpen, setConfigDialogOpen] = useState(false);
  const [calcDialogOpen, setCalcDialogOpen] = useState(false);
  const [itcDialogOpen, setItcDialogOpen] = useState(false);
  
  // Calculator state
  const [calcData, setCalcData] = useState({
    baseAmount: 10000,
    gstRate: 18,
    supplierState: '24',
    recipientState: '24'
  });
  const [calcResult, setCalcResult] = useState(null);
  
  // ITC form state
  const [itcForm, setItcForm] = useState({
    invoice_number: '',
    invoice_date: '',
    supplier_gstin: '',
    supplier_name: '',
    invoice_value: 0,
    igst: 0,
    cgst: 0,
    sgst: 0,
    itc_category: 'inputs'
  });
  
  // API calls
  const { data: suggestions, loading: loadingSuggestions } = useApi(
    () => gstAPI.getAISuggestions(),
    []
  );
  
  const { data: states } = useApi(() => gstAPI.getStates(), []);
  const { data: _sacCodes } = useApi(() => gstAPI.getSACCodes(), []);
  const { data: itcEntries, refresh: refreshITC } = useApi(
    () => gstAPI.getITCEntries(),
    []
  );
  const { data: tdsTcsEntries } = useApi(
    () => gstAPI.getTDSTCSEntries(),
    []
  );
  const { data: _ewayBills } = useApi(
    () => gstAPI.getEWayBills(),
    []
  );
  
  // GSTR summaries
  const { data: gstr1Summary, loading: loadingGSTR1, refresh: refreshGSTR1 } = useApi(
    () => gstAPI.getGSTR1Summary(selectedMonth, selectedYear),
    [selectedMonth, selectedYear]
  );
  
  const { data: gstr3bSummary, loading: loadingGSTR3B, refresh: refreshGSTR3B } = useApi(
    () => gstAPI.getGSTR3BSummary(selectedMonth, selectedYear),
    [selectedMonth, selectedYear]
  );
  
  const { data: liabilityForecast } = useApi(
    () => gstAPI.getLiabilityForecast(3),
    []
  );
  
  // Calculate GST
  const handleCalculate = async () => {
    try {
      const res = await gstAPI.calculateGST(
        calcData.baseAmount,
        calcData.gstRate,
        calcData.supplierState,
        calcData.recipientState
      );
      setCalcResult(res.data);
    } catch (error) {
      toast.error('Calculation failed');
    }
  };
  
  // Create ITC entry
  const handleCreateITC = async () => {
    try {
      await gstAPI.createITCEntry(itcForm);
      toast.success('ITC entry created');
      setItcDialogOpen(false);
      refreshITC();
      setItcForm({
        invoice_number: '',
        invoice_date: '',
        supplier_gstin: '',
        supplier_name: '',
        invoice_value: 0,
        igst: 0,
        cgst: 0,
        sgst: 0,
        itc_category: 'inputs'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ITC entry');
    }
  };
  
  // ITC columns
  const itcColumns = [
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'supplier_name', label: 'Supplier' },
    { key: 'supplier_gstin', label: 'GSTIN' },
    { key: 'invoice_value', label: 'Value', render: (row) => formatCurrency(row.invoice_value) },
    { key: 'igst', label: 'IGST', render: (row) => formatCurrency(row.igst) },
    { key: 'cgst', label: 'CGST', render: (row) => formatCurrency(row.cgst) },
    { key: 'sgst', label: 'SGST', render: (row) => formatCurrency(row.sgst) },
    { key: 'status', label: 'Status', render: (row) => (
      <Badge variant={row.status === 'claimed' ? 'default' : 'outline'}>
        {row.status}
      </Badge>
    )},
  ];
  
  // TDS columns
  const tdsColumns = [
    { key: 'section', label: 'Section' },
    { key: 'deductee_name', label: 'Deductee' },
    { key: 'deductee_pan', label: 'PAN' },
    { key: 'amount', label: 'Amount', render: (row) => formatCurrency(row.amount) },
    { key: 'rate', label: 'Rate', render: (row) => `${row.rate}%` },
    { key: 'tds_tcs_amount', label: 'TDS/TCS', render: (row) => formatCurrency(row.tds_tcs_amount) },
    { key: 'status', label: 'Status', render: (row) => (
      <Badge variant={row.status === 'deposited' ? 'default' : row.status === 'pending' ? 'destructive' : 'outline'}>
        {row.status}
      </Badge>
    )},
  ];
  
  const priorityColors = {
    high: 'destructive',
    medium: 'outline',
    low: 'secondary'
  };
  
  if (loadingSuggestions) return <PageSkeleton />;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="accounts-gst-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] flex items-center gap-2">
            <IndianRupee className="w-8 h-8" /> Accounts - GST Module
          </h1>
          <p className="text-muted-foreground mt-1">GST compliance, ITC tracking, and tax automation</p>
        </div>
        <div className="flex gap-2">
          <Select value={`${selectedMonth}`} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => <SelectItem key={m.value} value={`${m.value}`}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={`${selectedYear}`} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(y => <SelectItem key={y} value={`${y}`}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setCalcDialogOpen(true)}>
            <Calculator className="w-4 h-4 mr-2" /> Calculator
          </Button>
          {canManage && (
            <Button variant="outline" onClick={() => setConfigDialogOpen(true)}>
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
          )}
        </div>
      </div>

      {/* AI Suggestions Banner */}
      {suggestions?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-600" /> AI Tax Suggestions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {suggestions.slice(0, 3).map((s, idx) => (
                <div key={idx} className="flex items-start gap-3 p-2 bg-white/50 dark:bg-black/20 rounded">
                  <Badge variant={priorityColors[s.priority]}>{s.priority}</Badge>
                  <div>
                    <p className="font-medium">{s.title}</p>
                    <p className="text-sm text-muted-foreground">{s.description}</p>
                    {s.potential_savings && (
                      <p className="text-sm text-green-600">Potential savings: {formatCurrency(s.potential_savings)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="dashboard"><PieChart className="w-4 h-4 mr-1" /> Dashboard</TabsTrigger>
          <TabsTrigger value="gstr1"><FileText className="w-4 h-4 mr-1" /> GSTR-1</TabsTrigger>
          <TabsTrigger value="gstr3b"><FileSpreadsheet className="w-4 h-4 mr-1" /> GSTR-3B</TabsTrigger>
          <TabsTrigger value="itc"><TrendingUp className="w-4 h-4 mr-1" /> ITC</TabsTrigger>
          <TabsTrigger value="tds"><IndianRupee className="w-4 h-4 mr-1" /> TDS/TCS</TabsTrigger>
          <TabsTrigger value="eway"><Truck className="w-4 h-4 mr-1" /> E-Way</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Total Tax Liability</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{formatCurrency(gstr1Summary?.total_tax || 0)}</p>
                <p className="text-xs text-muted-foreground">For {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">ITC Available</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency((gstr3bSummary?.igst_itc_available || 0) + (gstr3bSummary?.cgst_itc_available || 0) + (gstr3bSummary?.sgst_itc_available || 0))}
                </p>
                <p className="text-xs text-muted-foreground">Claimable Input Tax Credit</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Net Payable</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(gstr3bSummary?.total_payable || 0)}</p>
                <p className="text-xs text-muted-foreground">After ITC adjustment</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">B2B Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{gstr1Summary?.b2b_invoices || 0}</p>
                <p className="text-xs text-muted-foreground">Taxable: {formatCurrency(gstr1Summary?.b2b_taxable_value || 0)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Forecast */}
          {liabilityForecast?.forecast && (
            <Card>
              <CardHeader>
                <CardTitle>Tax Liability Forecast</CardTitle>
                <CardDescription>AI-powered 3-month projection</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  {liabilityForecast.forecast.map((f, idx) => (
                    <div key={idx} className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        {MONTHS.find(m => m.value === f.month)?.label} {f.year}
                      </p>
                      <p className="text-xl font-bold">{formatCurrency(f.projected_liability)}</p>
                      <Badge variant="outline">{f.confidence} confidence</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* GSTR-1 Tab */}
        <TabsContent value="gstr1" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>GSTR-1 Summary - Outward Supplies</CardTitle>
                  <CardDescription>Period: {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}</CardDescription>
                </div>
                <Button variant="outline" onClick={refreshGSTR1} disabled={loadingGSTR1}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingGSTR1 ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {gstr1Summary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2">B2B Supplies (To Registered Persons)</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span>Invoices:</span><span className="font-medium">{gstr1Summary.b2b_invoices}</span>
                        <span>Taxable Value:</span><span className="font-medium">{formatCurrency(gstr1Summary.b2b_taxable_value)}</span>
                        <span>IGST:</span><span className="font-medium">{formatCurrency(gstr1Summary.b2b_igst)}</span>
                        <span>CGST:</span><span className="font-medium">{formatCurrency(gstr1Summary.b2b_cgst)}</span>
                        <span>SGST:</span><span className="font-medium">{formatCurrency(gstr1Summary.b2b_sgst)}</span>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2">B2C Supplies (To Unregistered)</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span>Invoices:</span><span className="font-medium">{gstr1Summary.b2c_invoices}</span>
                        <span>Taxable Value:</span><span className="font-medium">{formatCurrency(gstr1Summary.b2c_taxable_value)}</span>
                        <span>IGST:</span><span className="font-medium">{formatCurrency(gstr1Summary.b2c_igst)}</span>
                        <span>CGST:</span><span className="font-medium">{formatCurrency(gstr1Summary.b2c_cgst)}</span>
                        <span>SGST:</span><span className="font-medium">{formatCurrency(gstr1Summary.b2c_sgst)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-semibold mb-2">Total Outward Supplies</h4>
                    <div className="grid grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Taxable Value</p>
                        <p className="text-xl font-bold">{formatCurrency(gstr1Summary.total_taxable_value)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">IGST</p>
                        <p className="text-xl font-bold">{formatCurrency(gstr1Summary.total_igst)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">CGST</p>
                        <p className="text-xl font-bold">{formatCurrency(gstr1Summary.total_cgst)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">SGST</p>
                        <p className="text-xl font-bold">{formatCurrency(gstr1Summary.total_sgst)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No data available for selected period</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GSTR-3B Tab */}
        <TabsContent value="gstr3b" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>GSTR-3B Summary</CardTitle>
                  <CardDescription>Monthly Return Summary - {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}</CardDescription>
                </div>
                <Button variant="outline" onClick={refreshGSTR3B} disabled={loadingGSTR3B}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingGSTR3B ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {gstr3bSummary ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2">Output Tax Liability</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span>IGST:</span><span>{formatCurrency(gstr3bSummary.igst_liability)}</span></div>
                        <div className="flex justify-between"><span>CGST:</span><span>{formatCurrency(gstr3bSummary.cgst_liability)}</span></div>
                        <div className="flex justify-between"><span>SGST:</span><span>{formatCurrency(gstr3bSummary.sgst_liability)}</span></div>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2">ITC Available</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span>IGST:</span><span className="text-green-600">{formatCurrency(gstr3bSummary.igst_itc_available)}</span></div>
                        <div className="flex justify-between"><span>CGST:</span><span className="text-green-600">{formatCurrency(gstr3bSummary.cgst_itc_available)}</span></div>
                        <div className="flex justify-between"><span>SGST:</span><span className="text-green-600">{formatCurrency(gstr3bSummary.sgst_itc_available)}</span></div>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg bg-amber-50 dark:bg-amber-950/20">
                      <h4 className="font-semibold mb-2">Net Tax Payable</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between"><span>IGST:</span><span className="font-bold">{formatCurrency(gstr3bSummary.igst_payable)}</span></div>
                        <div className="flex justify-between"><span>CGST:</span><span className="font-bold">{formatCurrency(gstr3bSummary.cgst_payable)}</span></div>
                        <div className="flex justify-between"><span>SGST:</span><span className="font-bold">{formatCurrency(gstr3bSummary.sgst_payable)}</span></div>
                        <hr className="my-2" />
                        <div className="flex justify-between text-lg"><span>Total:</span><span className="font-bold text-amber-600">{formatCurrency(gstr3bSummary.total_payable)}</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No data available for selected period</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ITC Tab */}
        <TabsContent value="itc" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Input Tax Credit Entries</h3>
            <Button onClick={() => setItcDialogOpen(true)}>
              <Upload className="w-4 h-4 mr-2" /> Add ITC Entry
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <DataTable 
                data={itcEntries || []} 
                columns={itcColumns}
                emptyMessage="No ITC entries found"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TDS/TCS Tab */}
        <TabsContent value="tds" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">TDS/TCS Entries</h3>
            <Button>
              <Upload className="w-4 h-4 mr-2" /> Add TDS/TCS
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <DataTable 
                data={tdsTcsEntries || []} 
                columns={tdsColumns}
                emptyMessage="No TDS/TCS entries found"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* E-Way Bill Tab */}
        <TabsContent value="eway" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">E-Way Bills</h3>
            <Button>
              <Truck className="w-4 h-4 mr-2" /> Create E-Way Bill
            </Button>
          </div>
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No E-Way bills generated yet</p>
              <p className="text-sm mt-2">E-Way bills are required for goods movement exceeding ₹50,000</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* GST Calculator Dialog */}
      <Dialog open={calcDialogOpen} onOpenChange={setCalcDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>GST Calculator</DialogTitle>
            <DialogDescription>Calculate CGST/SGST or IGST based on supply type</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Base Amount (₹)</Label>
                <Input 
                  type="number" 
                  value={calcData.baseAmount}
                  onChange={(e) => setCalcData({...calcData, baseAmount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>GST Rate (%)</Label>
                <Select value={`${calcData.gstRate}`} onValueChange={(v) => setCalcData({...calcData, gstRate: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map(r => <SelectItem key={r} value={`${r}`}>{r}%</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Supplier State</Label>
                <Select value={calcData.supplierState} onValueChange={(v) => setCalcData({...calcData, supplierState: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(states || []).map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Recipient State</Label>
                <Select value={calcData.recipientState} onValueChange={(v) => setCalcData({...calcData, recipientState: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(states || []).map(s => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={handleCalculate} className="w-full">Calculate</Button>
            
            {calcResult && (
              <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                <p className="text-sm"><span className="text-muted-foreground">Supply Type:</span> <Badge>{calcResult.supply_type === 'inter_state' ? 'Inter-State (IGST)' : 'Intra-State (CGST+SGST)'}</Badge></p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span>Base Amount:</span><span className="font-medium">{formatCurrency(calcResult.base_amount)}</span>
                  {calcResult.supply_type === 'inter_state' ? (
                    <><span>IGST:</span><span className="font-medium">{formatCurrency(calcResult.igst)} ({calcData.gstRate}%)</span></>
                  ) : (
                    <>
                      <span>CGST:</span><span className="font-medium">{formatCurrency(calcResult.cgst)} ({calcData.gstRate/2}%)</span>
                      <span>SGST:</span><span className="font-medium">{formatCurrency(calcResult.sgst)} ({calcData.gstRate/2}%)</span>
                    </>
                  )}
                  <span className="font-bold">Total Amount:</span><span className="font-bold text-lg">{formatCurrency(calcResult.total_amount)}</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ITC Entry Dialog */}
      <Dialog open={itcDialogOpen} onOpenChange={setItcDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add ITC Entry</DialogTitle>
            <DialogDescription>Record Input Tax Credit from supplier invoice</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input value={itcForm.invoice_number} onChange={(e) => setItcForm({...itcForm, invoice_number: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Invoice Date</Label>
                <Input type="date" value={itcForm.invoice_date} onChange={(e) => setItcForm({...itcForm, invoice_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Supplier Name</Label>
                <Input value={itcForm.supplier_name} onChange={(e) => setItcForm({...itcForm, supplier_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Supplier GSTIN</Label>
                <Input value={itcForm.supplier_gstin} onChange={(e) => setItcForm({...itcForm, supplier_gstin: e.target.value})} maxLength={15} />
              </div>
              <div className="space-y-2">
                <Label>Invoice Value (₹)</Label>
                <Input type="number" value={itcForm.invoice_value} onChange={(e) => setItcForm({...itcForm, invoice_value: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={itcForm.itc_category} onValueChange={(v) => setItcForm({...itcForm, itc_category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inputs">Inputs</SelectItem>
                    <SelectItem value="capital_goods">Capital Goods</SelectItem>
                    <SelectItem value="input_services">Input Services</SelectItem>
                    <SelectItem value="inward_supplies">Inward Supplies</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>IGST (₹)</Label>
                <Input type="number" value={itcForm.igst} onChange={(e) => setItcForm({...itcForm, igst: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>CGST (₹)</Label>
                <Input type="number" value={itcForm.cgst} onChange={(e) => setItcForm({...itcForm, cgst: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>SGST (₹)</Label>
                <Input type="number" value={itcForm.sgst} onChange={(e) => setItcForm({...itcForm, sgst: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItcDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateITC}>Add Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
